import { useSuperAdminAllShops } from "@babackapp/private-api-client/client/use-super-admin-all-shops";
import {
  useSuperAdminConfig,
  useUpdateSuperAdminConfig,
} from "@babackapp/private-api-client/client/use-super-admin-shops";
import { Button } from "@babackapp/ui/components/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@babackapp/ui/components/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@babackapp/ui/components/popover";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronsUpDown, Loader2 } from "lucide-react";
import { useState } from "react";

const CONFIG_QUERY_KEY = ["get", "/super-admin/config", {}];

/**
 * The shop every super admin lands on at `/` (BAB-3035): no longer a member
 * of any shop, they would otherwise land on the first one of the list.
 */
export function SuperAdminDefaultShop() {
  const queryClient = useQueryClient();
  const { data: config, isPending } = useSuperAdminConfig();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const { data: shops } = useSuperAdminAllShops(isPickerOpen);
  const update = useUpdateSuperAdminConfig();

  const save = (shopId: string | null) => {
    setIsPickerOpen(false);
    update.mutate(
      { body: { superAdminDefaultShopId: shopId } },
      // The PATCH returns the whole config: no refetch needed.
      {
        onSuccess: (updated) =>
          queryClient.setQueryData(CONFIG_QUERY_KEY, updated),
      },
    );
  };

  const currentId = config?.superAdminDefaultShopId;

  return (
    <section className="rounded-lg border bg-card p-6 space-y-4">
      <div>
        <h2 className="text-base font-semibold">Boutique d'arrivée</h2>
        <p className="text-sm text-muted-foreground">
          Boutique ouverte par défaut quand un super admin arrive sur
          app.baback.co. Sans réglage, il arrive sur la dernière boutique
          ouverte dans son navigateur, sinon sur la première boutique active.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Popover open={isPickerOpen} onOpenChange={setIsPickerOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              className="w-80 justify-between"
              disabled={isPending || update.isPending}
            >
              <span className="truncate">
                {isPending
                  ? "Chargement…"
                  : currentId
                    ? (config?.superAdminDefaultShopName ??
                      `${currentId} (boutique introuvable)`)
                    : "Aucune (première boutique active)"}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-96 p-0" align="start">
            <Command>
              <CommandInput placeholder="Nom, domaine Shopify ou id…" />
              <CommandList className="max-h-72">
                {!shops ? (
                  <div className="flex justify-center p-3">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                ) : (
                  <>
                    <CommandEmpty>Aucune boutique.</CommandEmpty>
                    <CommandGroup>
                      {shops.map((shop) => (
                        <CommandItem
                          key={shop.id}
                          value={`${shop.name}|${shop.shopifyUrl ?? ""}|${shop.id}`}
                          onSelect={() => save(shop.id)}
                        >
                          <span className="flex-1 min-w-0">
                            <span className="block truncate">{shop.name}</span>
                            <span className="block truncate text-xs text-muted-foreground">
                              {shop.shopifyUrl ?? shop.id}
                            </span>
                          </span>
                          {shop.isActive === false && (
                            <span className="text-xs text-muted-foreground">
                              inactive
                            </span>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {currentId && (
          <Button
            variant="ghost"
            onClick={() => save(null)}
            disabled={update.isPending}
          >
            Retirer
          </Button>
        )}
        {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
      </div>

      {update.isError && (
        <p className="text-sm text-red-600">Enregistrement impossible.</p>
      )}
    </section>
  );
}
